/**
 * callService.js — Sesli sohbet API katmanı
 * ---------------------------------------------------------------------------
 * Bu dosya SAF API katmanıdır: sunucuya istek atar, SSE olaylarını dağıtır.
 * WebRTC mantığı `webrtcService.js` içindedir ve o dosya buradan
 * `sinyalGonder`'i kullanır. Bu yüzden burada webrtcService IMPORT EDİLMEZ —
 * aksi halde döngüsel bağımlılık oluşur.
 *
 * SUNUCU YÜKÜ İLKESİ:
 *   Bu dosyada POLLING YOKTUR ve eklenmemelidir. Sunucudan gelen her şey
 *   mevcut SSE bağlantısı üzerinden itilir. `aktifAramaAl` yalnızca sohbet
 *   odası AÇILIRKEN bir kez çağrılır — periyodik değil.
 * ---------------------------------------------------------------------------
 */

import { apiRequest } from "./apiClient";

/* ══════════════════════════════════════════════════════════════════════════
   ICE SUNUCULARI (STUN / TURN)
   ══════════════════════════════════════════════════════════════════════════ */

// Kimlikler sunucuda 1 saat geçerli üretilir; süresi dolmadan 5 dakika önce
// yenileriz ki arama tam kurulurken kimlik geçersizleşmesin.
const YENILEME_PAYI_MS = 5 * 60 * 1000;

let iceCache = null;

export async function iceSunuculariniAl(zorla = false) {
  const simdi = Date.now();
  if (!zorla && iceCache && iceCache.expiresAt - YENILEME_PAYI_MS > simdi) {
    return iceCache;
  }
  const data = await apiRequest("/calls/ice-servers");
  iceCache = {
    iceServers: data?.iceServers || [],
    expiresAt: Number(data?.expiresAt) || simdi + 55 * 60 * 1000,
    maxParticipants: Number(data?.maxParticipants) || 6,
  };
  return iceCache;
}

export function iceOnbelleginiTemizle() {
  iceCache = null;
}

/** RTCPeerConnection yapılandırması. */
export async function baglantiYapilandirmasiUret(hazirIceServers = null) {
  const iceServers = hazirIceServers || (await iceSunuculariniAl()).iceServers;
  return {
    iceServers,
    iceCandidatePoolSize: 2,
    bundlePolicy: "max-bundle",
    rtcpMuxPolicy: "require",
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   ARAMA YAŞAM DÖNGÜSÜ
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Arama başlatır. PREMIUM gerektirir.
 * Premium değilse sunucu 403 + code:"PREMIUM_REQUIRED" döner.
 */
export async function aramaBaslat(familyId, roomId, caller = {}) {
  return apiRequest("/calls", {
    method: "POST",
    body: JSON.stringify({
      familyId,
      roomId,
      callerName: String(caller?.name || "").slice(0, 60),
      callerAvatar: String(caller?.avatar || "").slice(0, 80),
    }),
  });
}

/** Aramaya katılır. Premium GEREKTİRMEZ. */
export async function aramayaKatil(callId) {
  return apiRequest(`/calls/${encodeURIComponent(callId)}/join`, { method: "POST" });
}

/**
 * Yerel mikrofon, WebRTC oturumu ve SSE hazır olduktan SONRA çağrılır.
 * Backend kullanıcıyı ancak bu noktada "joined" yapar ve diğer katılımcılara
 * teklif üretmeleri için haber verir. Böylece soğuk başlangıçta SDP kaybolmaz.
 */
export async function aramayaHazir(callId) {
  return apiRequest(`/calls/${encodeURIComponent(callId)}/ready`, { method: "POST" });
}

export async function aramayiReddet(callId) {
  return apiRequest(`/calls/${encodeURIComponent(callId)}/decline`, { method: "POST" });
}

export async function aramadanAyril(callId) {
  return apiRequest(`/calls/${encodeURIComponent(callId)}/leave`, { method: "POST" });
}

/**
 * Odada canlı arama var mı? Oda AÇILIRKEN bir kez çağrılır.
 * Periyodik olarak ÇAĞIRMAYIN — durum değişiklikleri SSE ile gelir.
 */
export async function aktifAramaAl(familyId, roomId) {
  const qs = `?familyId=${encodeURIComponent(familyId)}&roomId=${encodeURIComponent(roomId)}`;
  return apiRequest(`/calls/active${qs}`);
}

/**
 * Uygulama açılırken/öne gelirken kullanıcının cevap bekleyen çağrısını arar.
 * Oda bilinmeden çalışan tek seferlik uzlaştırmadır; polling yapılmaz.
 */
export async function bekleyenGelenAramaAl(familyId) {
  const qs = `?familyId=${encodeURIComponent(familyId)}`;
  return apiRequest(`/calls/pending-incoming${qs}`);
}

/** SDP / ICE sinyali iletir. webrtcService buradan çağırır. */
export async function sinyalGonder(callId, { to, kind, data, signalId = null }) {
  return apiRequest(`/calls/${encodeURIComponent(callId)}/signal`, {
    method: "POST",
    body: JSON.stringify({ to, kind, data, signalId }),
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   SSE HAZIRLIK KAPISI

   CallKit/Android bildirimiyle uygulama soğuk başlatıldığında REST istekleri
   WebView'den önce çalışabilir; ancak WebRTC teklifinin alınacağı SSE bağlantısı
   henüz kurulmamış olabilir. Katılımı "ready" yapmadan önce bu olay tabanlı
   kapı beklenir. Polling veya periyodik ağ isteği üretmez.
   ══════════════════════════════════════════════════════════════════════════ */

const sseDurumlari = new Map();

export function sseDurumuGuncelle(familyId, connected) {
  const key = String(familyId || "");
  if (!key) return;
  sseDurumlari.set(key, !!connected);
}

export function sseHazirMi(familyId) {
  return sseDurumlari.get(String(familyId || "")) === true;
}

export function sseHazirBekle(familyId, timeoutMs = 12000) {
  const key = String(familyId || "");
  if (!key || sseHazirMi(key)) return Promise.resolve(true);
  if (typeof window === "undefined") return Promise.resolve(false);

  return new Promise((resolve) => {
    let bitti = false;
    const tamamla = (sonuc) => {
      if (bitti) return;
      bitti = true;
      clearTimeout(timer);
      window.removeEventListener("fp:sseStatus", dinle);
      resolve(sonuc);
    };
    const dinle = (e) => {
      const d = e?.detail || {};
      if (String(d.familyId || "") === key && d.connected === true) tamamla(true);
    };
    const timer = setTimeout(() => tamamla(false), Math.max(1000, Number(timeoutMs) || 12000));
    window.addEventListener("fp:sseStatus", dinle);
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   iOS VoIP TOKEN (PushKit) — FAZ 4'te kullanılacak
   ══════════════════════════════════════════════════════════════════════════ */

export async function voipTokenKaydet({ deviceId, voipToken, familyId = null }) {
  if (!deviceId || !voipToken) return { ok: false };
  return apiRequest("/calls/voip-token", {
    method: "POST",
    body: JSON.stringify({ deviceId, voipToken, familyId }),
  });
}

export async function voipTokenSil(deviceId) {
  if (!deviceId) return { ok: false };
  return apiRequest("/calls/voip-token", {
    method: "DELETE",
    body: JSON.stringify({ deviceId }),
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   YETENEK VE İZİN
   ══════════════════════════════════════════════════════════════════════════ */

export function webrtcDesteniyorMu() {
  return !!(
    typeof window !== "undefined" &&
    window.RTCPeerConnection &&
    navigator?.mediaDevices?.getUserMedia
  );
}

/**
 * Mikrofon izni ister ve ses akışını döndürür.
 *
 * Yankı engelleme (AEC), gürültü bastırma (NS) ve otomatik kazanç (AGC)
 * WebRTC'nin yerleşik özellikleridir; kapatılırsa hoparlörden konuşurken
 * karşı taraf kendi sesini yankı olarak duyar.
 */
export async function mikrofonAkisiAl() {
  if (!webrtcDesteniyorMu()) {
    const e = new Error("WEBRTC_UNSUPPORTED");
    e.code = "WEBRTC_UNSUPPORTED";
    throw e;
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
  } catch (err) {
    const e = new Error("MIC_PERMISSION_DENIED");
    e.code = "MIC_PERMISSION_DENIED";
    e.cause = err;
    throw e;
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   SİNYAL OLAYLARI — mevcut SSE akışına köprü
   ══════════════════════════════════════════════════════════════════════════ */

const CALL_EVENT_PREFIX = "call:";
const aboneler = new Set();

/**
 * Arama olaylarına abone olur; aboneliği iptal eden fonksiyon döner.
 * Olay tipleri: call:incoming, call:signal, call:participant-joined,
 * call:participant-left, call:declined, call:ended
 */
export function aramaOlaylarinaAbone(handler) {
  if (typeof handler !== "function") return () => {};
  aboneler.add(handler);
  return () => aboneler.delete(handler);
}

/**
 * dataSyncService'in SSE dinleyicisinden çağrılır.
 * Arama olayıysa abonelere dağıtır ve true döner.
 */
export function sseOlayiniIsle(olay) {
  if (!olay || typeof olay.type !== "string") return false;
  if (!olay.type.startsWith(CALL_EVENT_PREFIX)) return false;

  aboneler.forEach((fn) => {
    try {
      fn(olay);
    } catch (_) {
      // Bir abonenin hatası diğerlerini etkilemesin
    }
  });
  return true;
}

export const CALL_LIMITS = { MAX_PARTICIPANTS: 6 };
